import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Typography, Space, Card, Spin, Tag, Tooltip } from 'antd';
import {
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  ClearOutlined,
  BulbOutlined,
  AudioOutlined,
  SoundOutlined,
  AudioMutedOutlined,
} from '@ant-design/icons';
import { sendChatMessage } from '../../services/adminService';

const { Title, Text } = Typography;

const SUGGESTED_QUESTIONS = [
  "Combien de RDV sont prévus demain ?",
  "Quel est le planning de l'EQUIPE 5 cette semaine ?",
  "Y a-t-il des optimisations possibles pour demain ?",
  "Quelle distance entre Leon et Madrid ?",
  "Quelles équipes sont absentes cette semaine ?",
  "Combien de RDV par équipe cette semaine ?",
];

const formatMarkdown = (text) => {
  if (!text) return '';

  // Process tables
  let html = text.replace(/\n/g, '\n');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Code inline
  html = html.replace(/`([^`]+)`/g, '<code style="background:#f5f5f5;padding:2px 6px;border-radius:3px;font-size:12px">$1</code>');

  // Tables: detect lines with | and convert
  const lines = html.split('\n');
  let inTable = false;
  const result = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith('|') && line.endsWith('|')) {
      // Check if next line is separator
      if (!inTable) {
        result.push('<div style="overflow-x:auto;margin:8px 0"><table style="border-collapse:collapse;width:100%;font-size:13px">');
        inTable = true;
      }

      // Skip separator lines (|---|---|)
      if (/^\|[\s\-:|]+\|$/.test(line)) continue;

      const cells = line.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      const isHeader = i + 1 < lines.length && /^\|[\s\-:|]+\|$/.test(lines[i + 1]?.trim());

      if (isHeader) {
        result.push('<thead><tr>');
        cells.forEach(c => result.push(`<th style="border:1px solid #e8e8e8;padding:6px 10px;background:#fafafa;text-align:left;white-space:nowrap">${c.trim()}</th>`));
        result.push('</tr></thead><tbody>');
      } else {
        result.push('<tr>');
        cells.forEach(c => result.push(`<td style="border:1px solid #e8e8e8;padding:6px 10px">${c.trim()}</td>`));
        result.push('</tr>');
      }
    } else {
      if (inTable) {
        result.push('</tbody></table></div>');
        inTable = false;
      }
      // Lists
      if (/^[-•]\s/.test(line)) {
        result.push(`<div style="padding:2px 0 2px 16px">• ${line.replace(/^[-•]\s/, '')}</div>`);
      } else if (/^\d+\.\s/.test(line)) {
        result.push(`<div style="padding:2px 0 2px 16px">${line}</div>`);
      } else if (line === '') {
        result.push('<div style="height:8px"></div>');
      } else {
        result.push(`<div>${line}</div>`);
      }
    }
  }
  if (inTable) {
    result.push('</tbody></table></div>');
  }

  return result.join('');
};

// Inject pulse animation style once
const PULSE_STYLE_ID = 'mic-pulse-style';
if (typeof document !== 'undefined' && !document.getElementById(PULSE_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes micPulse {
      0% { box-shadow: 0 0 0 0 rgba(255,77,79,0.5); }
      70% { box-shadow: 0 0 0 10px rgba(255,77,79,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,77,79,0); }
    }
  `;
  document.head.appendChild(style);
}

// Strip HTML/markdown for TTS
const stripForTTS = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\|/g, ' ')
    .replace(/[-]{3,}/g, '')
    .replace(/#{1,6}\s/g, '')
    .replace(/\n+/g, '. ')
    .replace(/\s+/g, ' ')
    .trim();
};

const speakText = (text, onEnd) => {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(stripForTTS(text));
  utterance.lang = 'fr-FR';
  utterance.rate = 1.0;
  // Try to pick a French voice
  const voices = window.speechSynthesis.getVoices();
  const frVoice = voices.find(v => v.lang.startsWith('fr'));
  if (frVoice) utterance.voice = frVoice;
  if (onEnd) utterance.onend = onEnd;
  window.speechSynthesis.speak(utterance);
};

const MessageBubble = ({ message, onSpeak }) => {
  const isUser = message.role === 'user';
  const [speaking, setSpeaking] = useState(false);

  const handleSpeak = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    } else {
      setSpeaking(true);
      speakText(message.content, () => setSpeaking(false));
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16,
    }}>
      <div style={{
        display: 'flex',
        gap: 10,
        maxWidth: '85%',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isUser ? '#059669' : '#1677ff',
          color: 'white', fontSize: 14, flexShrink: 0,
        }}>
          {isUser ? <UserOutlined /> : <RobotOutlined />}
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{
            background: isUser ? '#059669' : '#f5f5f5',
            color: isUser ? 'white' : '#333',
            padding: '10px 16px',
            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            lineHeight: 1.6,
            fontSize: 14,
          }}>
            {isUser ? (
              <div>{message.content}</div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }} />
            )}
          </div>
          {!isUser && (
            <Tooltip title={speaking ? 'Arrêter la lecture' : 'Écouter'}>
              <Button
                type="text"
                size="small"
                icon={speaking ? <AudioMutedOutlined /> : <SoundOutlined />}
                onClick={handleSpeak}
                style={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  fontSize: 12,
                  color: speaking ? '#ff4d4f' : '#8c8c8c',
                  background: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  padding: 0,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [ttsAutoEnabled, setTtsAutoEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const ttsAutoRef = useRef(ttsAutoEnabled);
  const pendingSendRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    ttsAutoRef.current = ttsAutoEnabled;
  }, [ttsAutoEnabled]);

  // Load voices early
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSend = useCallback(async (text) => {
    const question = text || input.trim();
    if (!question || loading) return;

    // Stop any ongoing TTS when user sends a new message
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    const userMessage = { role: 'user', content: question };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await sendChatMessage(newMessages);
      if (response.success && response.data) {
        setMessages(prev => [...prev, response.data]);
        // Auto TTS for AI response
        if (ttsAutoRef.current && response.data.content) {
          speakText(response.data.content);
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Désolé, une erreur s'est produite. Réessayez."
        }]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Erreur de connexion au service IA. Vérifiez votre connexion."
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages]);

  // Process pending send from speech recognition
  useEffect(() => {
    if (pendingSendRef.current && !loading) {
      const text = pendingSendRef.current;
      pendingSendRef.current = null;
      handleSend(text);
    }
  }, [loading, handleSend]);

  // Speech-to-Text
  const toggleListening = useCallback(() => {
    if (!SpeechRecognition) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur. Utilisez Chrome ou Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setInput(finalTranscript || interimTranscript);
      if (finalTranscript) {
        pendingSendRef.current = finalTranscript;
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Accès au microphone refusé. Autorisez le micro dans les paramètres du navigateur.');
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  const handleReset = () => {
    setMessages([]);
    setInput('');
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  return (
    <div style={{ height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <RobotOutlined style={{ fontSize: 24, color: '#1677ff' }} />
          <Title level={4} style={{ margin: 0 }}>Assistant IA - Planning RDV</Title>
        </Space>
        <Space>
          <Tooltip title={ttsAutoEnabled ? 'Désactiver la lecture auto' : 'Activer la lecture auto'}>
            <Button
              icon={ttsAutoEnabled ? <SoundOutlined /> : <AudioMutedOutlined />}
              onClick={() => {
                setTtsAutoEnabled(v => !v);
                if (ttsAutoEnabled && window.speechSynthesis) window.speechSynthesis.cancel();
              }}
              type={ttsAutoEnabled ? 'primary' : 'default'}
              style={ttsAutoEnabled ? { background: '#1677ff' } : {}}
            >
              Lecture auto
            </Button>
          </Tooltip>
          <Button icon={<ClearOutlined />} onClick={handleReset} disabled={messages.length === 0}>
            Nouvelle conversation
          </Button>
        </Space>
      </div>

      {/* Messages area */}
      <Card
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, overflow: 'auto', padding: '16px 20px' } }}
      >
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <RobotOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={5} style={{ color: '#8c8c8c' }}>
              Posez-moi vos questions sur les RDV et plannings
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Je peux consulter les données en temps réel : RDV, équipes, distances, optimisations...
            </Text>
            <div style={{ maxWidth: 500, margin: '0 auto' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                <BulbOutlined /> Suggestions :
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <Tag
                    key={i}
                    style={{
                      cursor: 'pointer', padding: '6px 12px', fontSize: 13,
                      borderRadius: 16, borderColor: '#1677ff', color: '#1677ff',
                    }}
                    onClick={() => handleSend(q)}
                  >
                    {q}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} onSpeak={speakText} />
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#1677ff', color: 'white', fontSize: 14,
                }}>
                  <RobotOutlined />
                </div>
                <div style={{
                  background: '#f5f5f5', padding: '10px 20px',
                  borderRadius: '16px 16px 16px 4px',
                }}>
                  <Spin size="small" /> <Text type="secondary" style={{ marginLeft: 8 }}>En train de réfléchir...</Text>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Card>

      {/* Input area */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <Input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={() => handleSend()}
          placeholder={isListening ? '🎤 Parlez maintenant...' : 'Posez votre question sur les RDV, équipes, distances...'}
          size="large"
          disabled={loading}
          autoFocus
        />
        <Tooltip title={isListening ? 'Arrêter l\'écoute' : 'Parler au micro'}>
          <Button
            icon={<AudioOutlined />}
            size="large"
            onClick={toggleListening}
            disabled={loading}
            style={{
              background: isListening ? '#ff4d4f' : undefined,
              borderColor: isListening ? '#ff4d4f' : undefined,
              color: isListening ? 'white' : undefined,
              animation: isListening ? 'micPulse 1.5s infinite' : undefined,
            }}
          />
        </Tooltip>
        <Button
          type="primary"
          icon={<SendOutlined />}
          size="large"
          onClick={() => handleSend()}
          loading={loading}
          style={{ background: '#059669', borderColor: '#059669' }}
        >
          Envoyer
        </Button>
      </div>
    </div>
  );
};

export default ChatPage;
